import { Box, Paper, Typography, useTheme, Grid, Divider, Stack, Chip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FaEnvelopeOpenText } from "react-icons/fa";
import SurveyDialog from "./SurveyDialog";

const ThankYou = ({ submissionData }) => {
    const theme = useTheme();
    const { t } = useTranslation();

    const app = submissionData?.payload || {};
    const schools = app.applicationSchools || [];

    const SummaryField = ({ label, value }) => (
        <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Typography variant="body1" fontWeight={500}>{value || "-"}</Typography>
        </Grid>
    );

    return (
        <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="100vh"
            bgcolor="primary.main"
        >
            <Paper
                elevation={3}
                sx={{
                    width: { xs: "100%", md: "95vw" },
                    minHeight: { xs: "100vh", md: "900px" },
                    p: 4,
                    borderRadius: { xs: 0, md: 3 },
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                }}
            >
                <FaEnvelopeOpenText
                    style={{
                        color: theme.palette.primary.main,
                        marginBottom: theme.spacing(5),
                    }}
                    size={"100px"}
                />
                <Typography variant="h4" mb={2}>
                    {t("Thank You!")}
                </Typography>
                <Typography variant="h6" fontWeight={500} color="text.secondary" m={3} px={5}>
                    {t("Your application has been submitted successfully.")}
                </Typography>
                <Typography variant="h6" fontWeight={500} color="text.secondary" px={5}>
                    {t("A confirmation email has been sent to the address you provided.")}
                </Typography>
                <Typography variant="h6" fontWeight={500} color="text.secondary" m={3} px={5}>
                    {t("You will receive an")} <b>{t("application number")}</b>{" "}
                    {t(
                        "for your records. After the lottery, you can use this number to check your"
                    )}{" "}
                    <b>{t("child’s application status")}</b> {t("online.")}
                </Typography>

                {/* Application Summary */}
                <Paper
                    elevation={1}
                    sx={{
                        mt: 5,
                        p: 3,
                        minWidth: { md: 800 },
                        bgcolor: "grey.100",
                        borderRadius: 2,
                    }}
                >
                    <Typography
                        variant="h6"
                        fontWeight={600}
                        color="primary.main"
                        mb={2}
                        textAlign="center"
                    >
                        {t("Application Summary")}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Grid container spacing={1}>
                        <SummaryField label={t("Application #")} value={app.id} />
                        <SummaryField label={t("Student Name")} value={`${app.firstName || ""} ${app.lastName || ""}`} />
                        <SummaryField label={t("Date of Birth")} value={app.dateOfBirth} />
                        <SummaryField label={t("Grade Applied For")} value={app.studentGr.name} />
                        <SummaryField label={t("Parent/Guardian Name")} value={`${app.parentFirstName || ""} ${app.parentLastName || ""}`} />
                        <SummaryField
                            label={t("Address")}
                            value={
                                app.address
                                    ? `${app.address.street || ""} ${app.address.line2 || ""}, ${app.address.city || ""}, ${app.address.state || ""} ${app.address.zip || ""}`
                                    : "-"
                            }
                        />
                        <SummaryField label={t("School Year")} value={app.academicYearProvider?.name} />

                        <Grid item xs={12} md={6}>
                            <Typography variant="body2" color="text.secondary">
                                {t("Schools Applied")}
                            </Typography>
                            {schools.length > 0 ? (
                                <Stack spacing={0.5} alignItems="center">
                                    {schools.map((item, index) => (
                                        <Chip size="small" key={index} label={item?.school?.name || item?.name || "-"} />
                                    ))}
                                </Stack>
                            ) : (
                                <Typography variant="body1" fontWeight={500}>-</Typography>
                            )}
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Typography variant="body2" color="text.secondary">
                                {t("Sibling Priority")}
                            </Typography>
                            {app.sibling ? (
                                <Typography variant="body1" fontWeight={500}>
                                    {t("Yes")} ({app.siblingFirstName} {app.siblingLastName})
                                </Typography>
                            ) : (
                                <Typography variant="body1" fontWeight={500}>{t("No")}</Typography>
                            )}
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="body2" color="text.secondary">
                                {t("Staff Priority")}
                            </Typography>
                            {app.staffPriority ? (
                                <Typography variant="body1" fontWeight={500}>
                                    {t("Yes")} ({app.staffEmail})
                                </Typography>
                            ) : (
                                <Typography variant="body1" fontWeight={500}>{t("No")}</Typography>
                            )}
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="body2" color="text.secondary">
                                {t("Economically Disadvantaged")}
                            </Typography>
                            {app.edcStudent ? (
                                <Typography variant="body1" fontWeight={500}>
                                    {t("Yes")}
                                </Typography>
                            ) : (
                                <Typography variant="body1" fontWeight={500}>{t("No")}</Typography>
                            )}
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="body2" color="text.secondary">
                                {t("ELL")}
                            </Typography>
                            {app.ellStudent ? (
                                <Typography variant="body1" fontWeight={500}>
                                    {t("Yes")}
                                </Typography>
                            ) : (
                                <Typography variant="body1" fontWeight={500}>{t("No")}</Typography>
                            )}
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="body2" color="text.secondary">
                                {t("Special Education")}
                            </Typography>
                            {app.swdStudent ? (
                                <Typography variant="body1" fontWeight={500}>
                                    {t("Yes")}
                                </Typography>
                            ) : (
                                <Typography variant="body1" fontWeight={500}>{t("No")}</Typography>
                            )}
                        </Grid>
                    </Grid>
                </Paper>

                <div id="jotform-embed" />
            </Paper>

            <SurveyDialog />
        </Box>
    );
};

export default ThankYou;
